import { z } from "zod";

/**
 * Site filter query (PRD §12.2 filter rail, §13 `GET /api/sites`). Shared by the web filter rail,
 * the table view, the id-set endpoint and exports so one URL-synced shape drives every view.
 *
 * Multi-value parameters arrive as comma-separated strings in the URL and are split here.
 */

const csv = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string") return v === "" ? [] : v.split(",").map((s) => s.trim());
    return undefined;
  }, z.array(item).optional());

const bool = z.preprocess((v) => {
  if (typeof v === "boolean") return v;
  if (v === "1" || v === "true") return true;
  if (v === "0" || v === "false") return false;
  return undefined;
}, z.boolean().optional());

export const Segment = z.enum(["A", "B", "D"]);
export const Tier = z.enum(["1", "2", "3", "x"]);
export const ContactStatus = z.enum(["none", "named", "verified"]);
export const ClimatePreset = z.enum(["hot_dry", "hot", "any_potential", "baseline_band"]);
export const SiteSort = z.enum(["score", "mw", "units", "name", "state", "year", "operator"]);

export const SiteFilters = z.object({
  bbox: z
    .preprocess((v) => {
      if (typeof v !== "string") return v;
      const parts = v.split(",").map(Number);
      return parts.length === 4 && parts.every(Number.isFinite) ? parts : undefined;
    }, z.tuple([z.number(), z.number(), z.number(), z.number()]).optional())
    .describe("west,south,east,north"),
  mw_min: z.coerce.number().min(0).max(2000).default(40),
  mw_max: z.coerce.number().min(0).max(2000).default(130),
  core: bool,
  pm: csv(z.string().max(4)),
  frame: csv(z.string().max(20)),
  oem: csv(z.string().max(40)),
  state: csv(z.string().length(2)),
  ba: csv(z.string().max(8)),
  sector: csv(z.coerce.number().int().min(1).max(7)),
  preset: ClimatePreset.optional(),
  tier: csv(Tier),
  cooling: csv(z.string().max(32)),
  contact: csv(ContactStatus),
  operator: z.string().max(120).optional(),
  year_min: z.coerce.number().int().min(1900).max(2100).optional(),
  year_max: z.coerce.number().int().min(1900).max(2100).optional(),
  status: csv(z.string().max(12)),
  mothballed: bool,
  segment: csv(Segment),
  bbm_ref: bool,
  q: z.string().max(120).optional(),
  list: z.coerce.number().int().positive().optional(),
  sort: SiteSort.optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(200).default(50),
});

export type SiteFilters = z.infer<typeof SiteFilters>;
export type SiteFiltersInput = z.input<typeof SiteFilters>;

/** Serialize a filter object back to URL query parameters (arrays become comma lists). */
export function filtersToParams(f: Partial<SiteFiltersInput>): URLSearchParams {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      p.set(k, v.map(String).join(","));
    } else if (v === true) {
      p.set(k, "1");
    } else {
      p.set(k, String(v));
    }
  }
  return p;
}
